import BaseDataObject from './BaseDataObject';
import Horse from './Horse';
import JockeyTrainer from './JockeyTrainer';
import Money from './Money';
import ShoeInfo from './ShoeInfo';
import Track from './Track';

export default class HorseInfo extends BaseDataObject {
  getStartNumber() {
    return this.getInteger('startNr');
  }

  getNumberOfPreviousResults() {
    return this.getNumberOf('prevHorseResults');
  }

  getPreviousResults() {
    return this.getObjects('prevHorseResults', HorseInfoPreviousResult);
  }

  getHorse() {
    return this.getObject('horse', Horse);
  }

  getJockey() {
    return this.getObject('driver', JockeyTrainer);
  }

  getTrainer() {
    return this.getObject('trainer', JockeyTrainer);
  }

  getHometrack() {
    return this.getObject('homeTrack', Track);
  }

  getDistance() {
    return this.getInteger('distance');
  }

  getPostPosition() {
    return this.getInteger('postPosition');
  }

  getRecordTime() {
    return this.getDate('recTime');
  }

  getRecordIsWinner() {
    return this.getBoolean('recIsWinner');
  }

  getRecordType() {
    return this.getObject('recType', RecordType);
  }

  getEarning() {
    return this.getObject('earning', Money);
  }

  getCurrentYearlyStats() {
    return this.getObject('currentHorseYearlyStat', YearlyStats);
  }

  getPreviousYearlyStats() {
    return this.getObject('prevHorseYearlyStat', YearlyStats);
  }

  getTotalYearlyStats() {
    return this.getObject('totalHorseYearlyStat', YearlyStats);
  }

  getExternalURL() {
    return this.getString('externalUrl');
  }

  getHorseAnalysis() {
    return this.getObject('horseAnalysis', HorseAnalysis);
  }

  getErrorFetchingHorseAnalysis() {
    return this.getBoolean('errorFetchingHorseAnalysis');
  }

  getNumberOfRaceComments() {
    if (this.data.raceComments && this.data.raceComments.previousSwedishStart) {
      return this.data.raceComments.previousSwedishStart.length;
    }

    return 0;
  }

  getRaceComment(index) {
    if (this.data.raceComments && this.data.raceComments.previousSwedishStart) {
      const data = this.data.raceComments.previousSwedishStart[index];

      if (data) {
        return new RaceComment(data);
      }
    }
  }

  getEquipment(current, type) {
    if (this.data.raceComments && this.data.raceComments[current]) {
      if (type == null) {
        return this.data.raceComments[current];
      }
      const data = this.data.raceComments[current][type];
      if (data) {
        return new Equipment(data);
      }
    }
    return type ? undefined : [];
  }

  getStatistics(type, id) {
    if (
      this.data.raceComments &&
      this.data.raceComments.statistics &&
      type !== 'bridle'
    ) {
      const actualtype = type === 'carriage' ? 'carriages' : type;

      if (id == null) {
        return Object.keys(this.data.raceComments.statistics[actualtype]);
      }
      const data = this.data.raceComments.statistics[actualtype][id];
      if (data) {
        return new Statistics(data);
      }
    }
    return id ? undefined : [];
  }

  isAnalysisAvailable() {
    return this.getBoolean('horseAnalysisAvailable');
  }
}

class YearlyStats extends BaseDataObject {
  getYear() {
    return this.getInteger('year');
  }

  getNumberOfStarts() {
    return this.getInteger('nrOfStarts');
  }

  getNumberOfFirstPlaces() {
    return this.getInteger('firstPlace');
  }

  getNumberOfSecondPlaces() {
    return this.getInteger('secondPlace');
  }

  getNumberOfThirdPlaces() {
    return this.getInteger('thirdPlace');
  }
}

class RecordType extends BaseDataObject {
  getCode() {
    return this.getString('code');
  }

  getDescription() {
    return this.getString('domesticText');
  }
}

class HorseInfoPreviousResult extends BaseDataObject {
  getHorse() {
    return this.getObject('horse', Horse);
  }

  getJockey() {
    return this.getObject('driver', JockeyTrainer);
  }

  getDate() {
    return super.getDate('date');
  }

  getShoeInfo() {
    return this.getObject('shoeInfo', ShoeInfo);
  }

  getSulkyInfo() {
    return this.getString('sulkyInfo');
  }

  getTrackState() {
    return this.getObject('trackState', TrackState);
  }

  getDistance() {
    return this.getInteger('distance');
  }

  getEarning() {
    return this.getObject('earning', Money);
  }

  getFirstPrize() {
    return this.getObject('firstPrize', Money);
  }

  getFormattedTime() {
    return this.getString('formattedTime');
  }

  getFormattedResult() {
    return this.getString('formattedResult');
  }

  getOdds() {
    return this.getString('odds');
  }

  getDisqualified() {
    return this.getBoolean('disqualified');
  }

  getPlace() {
    return this.getInteger('place');
  }

  getPostPosition() {
    return this.getInteger('postPosition');
  }

  getRaceType() {
    return this.getString('raceType');
  }

  getTrack() {
    return this.getObject('track', Track);
  }

  getRaceNumber() {
    return this.getInteger('raceNr');
  }

  getIsMonte() {
    return this.getBoolean('isMonte');
  }
}

class TrackState extends BaseDataObject {
  getCode() {
    return this.getString('code');
  }

  getText() {
    return this.getString('domesticText');
  }
}

class HorseAnalysis extends BaseDataObject {
  getChangesHTML() {
    return this.getString('changes');
  }

  getConclusionHTML() {
    return this.getString('conclusion');
  }

  getStatistics() {
    return this.data.stat_texts || [];
  }

  getHeadToHead() {
    return this.data.mutuals || {};
  }
}

class Statistics extends BaseDataObject {
  getTotal() {
    return this.getInteger('count');
  }

  getPlace1() {
    return this.getInteger('plac1');
  }

  getPlace2() {
    return this.getInteger('plac2');
  }

  getPlace3() {
    return this.getInteger('plac3');
  }

  getName() {
    return this.getString('name');
  }

  getSummary() {
    return `(${this.getTotal()} ${this.getPlace1()}-${this.getPlace2()}-${this.getPlace3()})`;
  }
}
class Equipment extends BaseDataObject {
  getFirstTime() {
    return this.getBoolean('firstTime');
  }

  getId() {
    return this.getString('id');
  }

  getName() {
    return this.getString('name');
  }
}

class RaceComment extends BaseDataObject {
  getComment() {
    return this.getString('comment');
  }

  getScratched() {
    return this.getBoolean('scratched');
  }

  getScratchedReason() {
    return this.getString('scratchedReason');
  }

  getStartId() {
    return this.getInteger('id');
  }

  getDate() {
    if (typeof this.data.race?.meet?.date === 'string') {
      const parts = this.data.race.meet.date
        .split('-')
        .map(s => Number.parseInt(s, 10))
        .filter(Boolean);
      if (parts.length === 3) {
        const [year, month, day] = parts;
        const date = new Date();
        date.setFullYear(year, month - 1, day);
        return date;
      }
    }
  }

  getRaceNumber() {
    if (this.data.race) {
      return Number.parseInt(this.data.race.raceNumber, 10);
    }
  }

  getTrackCode() {
    if (this.data.race && this.data.race.meet) {
      return String(this.data.race.meet.track.id);
    }
  }

  getTrackName() {
    return this.data.race?.meet?.track.name;
  }

  getRace() {
    if (this.data.race) {
      return new RaceInformation(this.data.race);
    }
  }
}

class RaceInformation extends BaseDataObject {
  getFirst500() {
    return this.getFloat('time500');
  }

  getLast500() {
    return this.getFloat('timeLast500');
  }

  getFirst1000() {
    return this.getFloat('time1000');
  }

  getFirst2000() {
    return this.getFloat('time2000');
  }

  getNumberOfTopHorses() {
    if (this.data.results) {
      return this.data.results.length;
    }
  }

  getTopHorse(index) {
    const data = this.data.results?.[index];
    if (data) {
      return new RaceCommentTopHorse(data);
    }
  }
}

class RaceCommentTopHorse extends BaseDataObject {
  getName() {
    return this.data.horse.name;
  }

  getPlace() {
    return this.getInteger('placement');
  }

  getSpeed() {
    return this.getFloat('speed');
  }

  getDistance() {
    return this.getInteger('distance');
  }
}
